import React from 'react';
import { Box, Divider } from '@mui/material';
import { useQueryParams } from '@motivo/guanyin/src/hooks/useQueryParams';
import { EmptyOrLoadingState } from '@motivo/guanyin/src/components';
import { useQuery } from '@apollo/client';
import { ResultOf } from ':src/graphql';
import SessionsFilters from './SessionsFilters';
import AvatarHeader from './AvatarHeader';
import { sessionsQuery, userByIdQuery } from '../queries';
import SessionsTable from './SessionsTable';

type SessionsQueryQuery = ResultOf<typeof sessionsQuery>;

type SessionsCardProps = {
  sessionData: SessionsQueryQuery['sessions'] | undefined;
  organizationId: number;
  sessionLoading: boolean;
  shouldDisplaySessionNotes: boolean;
};

export default function SessionsCard({
  sessionData,
  organizationId,
  sessionLoading,
  shouldDisplaySessionNotes,
}: SessionsCardProps) {
  const sessions = sessionData?.result;
  const { queryParams } = useQueryParams();
  const { userId } = queryParams;

  const { data: userData, loading: userQueryLoading } = useQuery(userByIdQuery, {
    variables: {
      id: Number(userId),
      deletedForOrganizationId: organizationId,
    },
    skip: !userId,
  });

  const user = userData?.userById;

  const hasSelectedSupervisor = userId
    ? sessions?.some((session) => session.createdBy === Number(userId))
    : false;

  return (
    <>
      <Box padding="2rem">
        <SessionsFilters
          organizationId={organizationId}
          shouldShowNotesExport={shouldDisplaySessionNotes}
        />
      </Box>
      <Divider />
      {userQueryLoading || sessionLoading || !sessions ? (
        <Box py={4} px="2rem">
          <EmptyOrLoadingState loading />
        </Box>
      ) : (
        <>
          {user && (
            <>
              <Box padding="2rem">
                <AvatarHeader user={user} isSupervisor={Boolean(hasSelectedSupervisor)} />
              </Box>
              <Divider />
            </>
          )}
          <Box padding="2rem" pt={0}>
            <SessionsTable
              userId={user?.id}
              sessionData={sessionData}
              sessionLoading={sessionLoading}
              hasSelectedSupervisor={Boolean(hasSelectedSupervisor)}
              userQueryLoading={userQueryLoading}
              shouldDisplaySessionNotes={shouldDisplaySessionNotes}
            />
          </Box>
        </>
      )}
    </>
  );
}
